import React from 'react';
import { state, props } from 'app/pwa/app.cerebral';
import { connect, ConnectedProps } from '@cerebral/react';
import { CHALLENGE_EVENT_TYPE } from 'resources/constants';

import EngagerSent from '../eventTypes/EngagerSent';
import PositionChanged from '../eventTypes/PositionChanged';
import ChallengeEnded from '../eventTypes/ChallengeEnded';
import ChallengeStarted from '../eventTypes/ChallengeStarted';
import UserJoined from '../eventTypes/UserJoined';
import ParticipantJoined from '../eventTypes/ParticipantJoined';
import UserGoalCompleted from '../eventTypes/UserGoalCompleted';
import ParticipantGoalCompleted from '../eventTypes/ParticipantGoalCompleted';
import ParticipantGoalSet from '../eventTypes/ParticipantGoalSet';
import ActivitiesLogged from '../eventTypes/ActivitiesLogged';
import QuizCompleted from '../eventTypes/QuizCompleted';

import dayjs from 'dayjs';
import { useText } from 'common/hooks';

type Props = {
  // eslint-disable-next-line react/no-unused-prop-types -- Does not detect use in props
  readonly eventKey: string;
};

const deps = {
  event: state.challenge.events.events[props`eventKey`],
  userProfileKey: state.appContainer.user.uid,
};

export function Event({
  event,
  userProfileKey,
}: Props & typeof deps & ConnectedProps): React.ReactElement {
  const t = useText();

  switch (event.type) {
    case CHALLENGE_EVENT_TYPE.NOTIFICATION:
      return (
        t.has(
          `messages.push_notifications.morning_message_engager.${event.campaignTopic}.descriptions.${event.currentDayInChallenge}`
        ) && (
          <EngagerSent
            time={dayjs(event.datetime).fromNow()}
            title={t('challenge.latest_events.FeC008.notification')}
            notificationText={t(
              `messages.push_notifications.morning_message_engager.${event.campaignTopic}.descriptions.${event.currentDayInChallenge}`
            )}
          />
        )
      );
    case CHALLENGE_EVENT_TYPE.POSITION_CHANGE:
      return (
        <PositionChanged
          datetime={event.datetime}
          newPosition={event.position}
          previousPosition={event.previousPosition}
        />
      );
    case CHALLENGE_EVENT_TYPE.CHALLENGE_END:
      return <ChallengeEnded datetime={event.datetime} />;

    case CHALLENGE_EVENT_TYPE.CHALLENGE_START:
      return <ChallengeStarted datetime={event.datetime} />;

    case CHALLENGE_EVENT_TYPE.PARTICIPANT_JOINED:
      if (event.profileKey === userProfileKey) {
        return (
          <UserJoined
            joinedDatetime={event.datetime}
            profileKey={event.profileKey}
            teamKey={event.teamKey}
            sectionKey={event.sectionKey}
          />
        );
      }

      return (
        <ParticipantJoined
          joinedDatetime={event.datetime}
          profileKey={event.profileKey}
        />
      );
    case CHALLENGE_EVENT_TYPE.GOALS_REACHED:
      if (event.profileKey === userProfileKey) {
        return (
          <UserGoalCompleted
            datetime={event.datetime}
            level={event.level}
            profileKey={event.profileKey}
          />
        );
      }

      return (
        <ParticipantGoalCompleted
          datetime={event.datetime}
          level={event.level}
          profileKey={event.profileKey}
        />
      );
    case CHALLENGE_EVENT_TYPE.GOALS_SET:
      return (
        <ParticipantGoalSet
          datetime={event.datetime}
          level={event.level}
          profileKey={event.profileKey}
        />
      );
    case CHALLENGE_EVENT_TYPE.DAILY_SUMMARY_UPDATES:
      return (
        <ActivitiesLogged
          eventActivities={Object.keys(event.activitiesCount || {})}
          profileKey={event.profileKey}
          values={{ co2: event.co2, points: event.points }}
          lastTimeLoggedAt={event.datetime}
        />
      );
    case CHALLENGE_EVENT_TYPE.QUIZ_COMPLETED:
      return (
        <QuizCompleted
          profileKey={event.profileKey}
          score={event.score}
          datetime={event.datetime}
          category={event.category}
        />
      );
    default:
      return null;
  }
}

export default connect<Props, typeof deps>(deps, Event);
